/* eslint-disable indent */
import { useEffect, useState, useRef } from 'preact/hooks';
import { html } from 'htm/preact';
import classNames from 'classnames';

import { get } from '../../utilities/fetch';
import scrollToTarget from '../../utilities/scroll-to-target-adjusted';
import { bindAll } from 'lodash-es';

const POSTS_PER_PAGE = 20;
const PAGINATION_LIMIT = 1;

/**
 * get current language from document
 * @returns {String}
 */
function getCurrentLang() {
  const lang = document.documentElement.lang;
  if (lang === 'en-GB') {
    return 'en';
  }
  return lang;
}

/**
 * get translation for given lang. Returns given text if translation not found
 * @param {string} text
 * @param {string} lang
 * @returns {string}
 */
function getTranslation(text, lang = getCurrentLang()) {
  const translations = {
    fi: {
      'Word Search …': 'Sanahaku …',
      Publish: 'Julkaistu',
      Valid: 'Voimassa',
      Name: 'Julkaisun nimi',
      Download: 'Lataa',
      'Order by date, desc': 'Järjestä pvm mukaan, laskeva',
      'Order by date, asc': 'Järjestä pvm mukaan, nouseva',
      Search: 'Hae',
      chosen: 'valittu',
      Found: 'Löytyi',
      results: 'tulosta',
      'Download pdf': 'Lataa pdf',
    },
  };

  if (!translations[lang] || !translations[lang][text]) {
    return text;
  }

  return translations[lang][text];
}

/**
 * Remove given value from array
 * @param {Array} arr to value be removed from
 * @param {integer|string} value to be removed
 * @returns {Array} new array
 */
 function arrayRemove(arr, value) {
  return arr.filter((ele) => ele !== value);
}

/**
 * Checkbox element
 * @param {string} name of the selection
 * @param {string} value of the selection
 * @param {string} filterName of the selection
 * @param {function} onChangeAction of the selection
 * @return {html}
 */
 function CheckBox({
   name,
   value,
   filterName,
   onChangeAction,
  }) {
  const [isChecked, setIsChecked] = useState(false);

  function onChange(options) {
    setIsChecked(options.boolean);
    onChangeAction(options);
  }

  return html`
    <label>
      <input
        id="${value}-identifier"
        type="checkbox"
        checked="${isChecked}"
        onChange="${() => { onChange({ name: filterName, value, boolean: !isChecked }); }}"
      />
      ${name}
    </label>
  `;
}

/**
 * Dropdown
 * @param {*} title of the dropdown
 * @param {*} children dropdown content
 * @returns {html}
 */
function Dropdown({ title, children }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  function dropdownOnClick() {
    setIsOpen(!isOpen);
  }

  const dropdownClasses = classNames('dropdown', {
    'dropdown--open': isOpen,
  });

  return html`
    <div class="${dropdownClasses}" ref="${wrapperRef}">
      <div class="dropdown__title" onClick="${dropdownOnClick}">
        <div>${title}</div>
        <span class="icon"></span>
      </div>
      <div class="dropdown__items">${children}</div>
    </div>
  `;
}

export default function AssessmentFetcher({ rootUrl = '' }) {
  const [isLoading, setIsLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState('date_desc');
  const [totalPosts, setTotalPosts] = useState(-1);
  const [totalPages, setTotalPages] = useState(-1);
  const [activePage, setActivePage] = useState(0);
  const [searchTerms, setSearchTerms] = useState({
    term: '',
    platform: [],
    user: [],
    language: [],
    use: [],
    type: [],
  });
  const root = useRef(null);

  const [currentQueryTerms, setCurrentQueryTerms] = useState([]);

  const totalPagesArray = [...Array(totalPages + 1).keys()].slice(1);
  const pagesBetweenOneAndLastArray = totalPagesArray.slice(1).slice(0, -1);

  const currentPageIndex = pagesBetweenOneAndLastArray.findIndex((i) => i === activePage);
  const startCurrentPagesIndex = Math.max(0, currentPageIndex);

  const isIndexNearEnd = startCurrentPagesIndex
    > pagesBetweenOneAndLastArray.length - (1 + PAGINATION_LIMIT);

  const currentPagesArray = isIndexNearEnd
    ? pagesBetweenOneAndLastArray.slice(-1 * PAGINATION_LIMIT)
    : pagesBetweenOneAndLastArray.slice(
      startCurrentPagesIndex,
      startCurrentPagesIndex + PAGINATION_LIMIT,
    );

  const lastPageOfTotalPages = totalPagesArray.slice(-1)[0];
  const lastPageOfCurrentPages = currentPagesArray.slice(-1)[0];
  const hasPagesBeforeCurrentPagination = (currentPagesArray[0] > 2);
  const hasPagesAfterCurrentPagination = (lastPageOfCurrentPages < lastPageOfTotalPages - 1);

  async function fetchAssessments({ page, sort = 'date_desc', overrideTerms = {} }) {
    const params = {
      ...searchTerms,
      ...overrideTerms,
      page,
      per_page: POSTS_PER_PAGE,
      sort,
      lang: getCurrentLang(),
    };

    setIsLoading(true);

    try {
      const result = await get(`${rootUrl}/wp-json/ppshp/v1/search_assessments`, { data: params });

      setCurrentQueryTerms(searchTerms);
      setPosts(result.assessments);
      setTotalPages(parseInt(result.pages, 10));
      setTotalPosts(parseInt(result.total_posts, 10));
    } catch (err) {
      // TODO: Error.
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchFilters() {
    setIsLoading(true);
    const params = {
      lang: getCurrentLang(),
    };

    try {
      const result = await get(`${rootUrl}/wp-json/ppshp/v1/assessment_filters`, { data: params });
      setFilters(result.filters);
    } catch (err) {
      // TODO: Error.
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchFilters();
    setActivePage(1);
    fetchAssessments({ page: 1 });
  }, []);

  async function handlePaginationClick({ page }) {
    setActivePage(page);
    setSearchTerms(currentQueryTerms);
    await fetchAssessments({ page, overrideTerms: currentQueryTerms });

    if (root.current) {
      scrollToTarget({
        targetEl: root.current,
        offset: -400,
      });
    }
  }

  function onTermChange(e) {
    setSearchTerms({
      ...searchTerms,
      term: e.target.value,
    });
  }

  function onFilterChange({ name, value, boolean }) {
    // remove item
    if (!boolean) {
      return setSearchTerms({
        ...searchTerms,
        [name]: arrayRemove(searchTerms[name], value),
      });
    }
    // add item
    return setSearchTerms({
      ...searchTerms,
      [name]: [...searchTerms[name], value],
    });
  }

  function onSortChange(value) {
    setSorting(value);
    setSearchTerms(currentQueryTerms);
    fetchAssessments({ page: 1, sort: value, overrideTerms: currentQueryTerms });
  }

  function onFormSubmit(e) {
    e.preventDefault();
    fetchAssessments({ page: 1 });
  }

  function getDropdownTitle(filter) {
    if (searchTerms[filter.name].length > 0) {
      return `${filter.label}, ${getTranslation('chosen')} ${searchTerms[filter.name].length}`;
    }
    return filter.label;
  }

  function setpdfDownloads(item) {
    if (!item.downloads.english && !item.downloads.swedish && !item.pdf_file) {
      return null;
    }
    let downloadButtons = [];
    if(item.pdf_file) {
      downloadButtons.push(html`<a target="_blank" href="${item.pdf_file}" class="finnish" download="true">
        <span>Lataa PDF</span> <svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor"><g><rect fill="none" height="24" width="24"/></g><g><path d="M18,15v3H6v-3H4v3c0,1.1,0.9,2,2,2h12c1.1,0,2-0.9,2-2v-3H18z M17,11l-1.41-1.41L13,12.17V4h-2v8.17L8.41,9.59L7,11l5,5 L17,11z"/></g></svg>
      </a>`);
    }
    if(item.downloads.english) {
      downloadButtons.push(html`<a target="_blank" href="${item.downloads.english}" class="finnish" download="true">
        <span>Download PDF</span> <svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor"><g><rect fill="none" height="24" width="24"/></g><g><path d="M18,15v3H6v-3H4v3c0,1.1,0.9,2,2,2h12c1.1,0,2-0.9,2-2v-3H18z M17,11l-1.41-1.41L13,12.17V4h-2v8.17L8.41,9.59L7,11l5,5 L17,11z"/></g></svg>
      </a>`);
    }
    if(item.downloads.swedish) {
      downloadButtons.push(html`<a target="_blank" href="${item.downloads.swedish}" class="finnish" download="true">
        <span>Laddar PDF</span> <svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor"><g><rect fill="none" height="24" width="24"/></g><g><path d="M18,15v3H6v-3H4v3c0,1.1,0.9,2,2,2h12c1.1,0,2-0.9,2-2v-3H18z M17,11l-1.41-1.41L13,12.17V4h-2v8.17L8.41,9.59L7,11l5,5 L17,11z"/></g></svg>
      </a>`);
    }
    return downloadButtons;
  }

  const containerClasses = classNames('assessments-root__container', {
    'is--loading': isLoading,
  });

  const isActivePageFirst = (activePage === totalPagesArray.slice(0, 1)[0]);
  const isActivePageLast = (activePage === totalPagesArray.slice(-1)[0]);

  return html`
    <div className=${containerClasses} ref=${root}>

      <form
        className="assessment-filters"
        data-action="assessment-search"
        onSubmit=${onFormSubmit}
      >
        <div class="input-row">
          <label class="screen-reader-text" for="search_term-identifier">Sanahaku</label>
          <input
            id="search_term-identifier"
            type="text"
            name="search_term"
            placeholder="${getTranslation('Word Search …')}"
            value="${searchTerms.term}"
            onChange=${onTermChange}
          />
        </div>

        ${filters
          .filter((filter) => 'choices' in filter)
          .map((filter) => {
            const title = getDropdownTitle(filter);
            return html`<div class="input-row-dropdown">
              <${Dropdown} title="${title}">
                ${Object.keys(filter.choices).map((key) => CheckBox({
                  name: filter.choices[key],
                  value: key,
                   filterName: filter.name,
                    onChangeAction: onFilterChange,
                  }))}
              </${Dropdown}>
            </div>`;
        })}

        <div class="input-row input-row--assessment-filter-submit">
          <button class="button" type="submit">
            <div class="button__contents">
              <span class="title">${getTranslation('Search')}</span>
              <span class="icon  icon--on-right">
                <svg focusable="false" viewBox="0 0 24 24" height="24" width="24"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path></svg>
              </span>
            </div>
          </button>
        </div>
      </form>

      <div className="assessment__post-count">
        ${totalPosts >= 0 && html`${getTranslation('Found')} ${totalPosts} ${getTranslation('results')}`}
      </div>

      <div className="assessment__sorting">
        <form class="assessment__sort-options" onSubmit=${() => false}>
          <div className="input-row">
            <div class="select">
              <select
                name="sorting[]"
                id="sorting-identifier"
                onChange=${(e) => { onSortChange(e.target.value); }}
                value=${sorting}
              >
                <option value="date_desc">${getTranslation('Order by date, desc')}</option>
                <option value="date_asc">${getTranslation('Order by date, asc')}</option>
              </select>
              <span className="icon"></span>
            </div>
          </div>
        </form>
      </div>

      <div class="assessment_headings">
        <div class="assessment_headings-publishdate"><h4>${getTranslation('Publish')}</h4></div>
        <div class="assessment_headings-expirydate"><h4>${getTranslation('Valid')}</h4></div>
        <div class="assessment_headings-publishname"><h4>${getTranslation('Name')}</h4></div>
      </div>

      <div className="assessments__listing">
        ${posts.map((post) => html`
          <div class="assessment-search__list-item">
            <div class="color color--${post.color}" />
            <div class="assessment-list-item-wrapper">
              <div class="date"><span class="date__title">${getTranslation('Publish')}</span> ${post.date}</div>
              <div class="expiry_date ${post.isoutdated ? 'expiry_date_red' : ''}">
                ${post.outdated_date ? html`<span class="date__title">${getTranslation('Valid')}</span> ${post.outdated_date}` : null}
              </div>
              <a href="${post.url}" class="titles">
                <div class="finnish">${post.title}</div>
              </a>
              <div class="download-buttons">
                ${setpdfDownloads(post)}
              </div>
            </div>
          </div>
        `)}
      </div>

      ${totalPagesArray.length > 1 && html`
        <div className="articles__pagination">
          <button
            type="button"
            class="button  button--square  button--prev"
            disabled=${isActivePageFirst}
            onClick=${() => {
              handlePaginationClick({ page: activePage - 1 });
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M15.41 16.09l-4.58-4.59 4.58-4.59L14 5.5l-6 6 6 6z "/>
            </svg>
          </button>

          <button
            type="button"
            class="button  button--square"
            data-active=${activePage === 1}
            onClick=${() => {
              handlePaginationClick({ page: 1 });
            }}
          >
            1
          </button>

          ${hasPagesBeforeCurrentPagination && html`
            <span className="ellipses">…</span>
          `}

          ${currentPagesArray.map((item) => html`
            <button
              type="button"
              class="button  button--square"
              data-active=${activePage === item}
              onClick=${() => {
                handlePaginationClick({ page: item });
              }}
            >
              ${item}
            </button>
          `)}

          ${hasPagesAfterCurrentPagination && html`
            <span className="ellipses">…</span>
          `}

          <button
            type="button"
            class="button  button--square"
            data-active=${activePage === lastPageOfTotalPages}
            onClick=${() => {
              handlePaginationClick({ page: lastPageOfTotalPages });
            }}
          >
            ${lastPageOfTotalPages}
          </button>

          <button
            type="button"
            class="button  button--square  button--next"
            disabled=${isActivePageLast}
            onClick=${() => {
              handlePaginationClick({ page: activePage + 1 });
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z "/>
            </svg>
          </button>
        </div>`
      }
    </div>
  `;
}
